import type { InviteResponse, PortalRole, PortalTeam, TeamInvite, TeamUser } from '@central/api';
import {
  Card,
  InviteLink,
  StatusChip,
  color,
  formatAgo,
  formatDate,
  isLastOwner,
  mfaLabel,
  radius,
} from '@central/ui';
import {
  Alert,
  Button,
  Group,
  Modal,
  SegmentedControl,
  Select,
  Stack,
  Switch,
  Table,
  Text,
  TextInput,
} from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent, type ReactNode } from 'react';
import { api, teamQuery } from '../api';
import { QueryState } from '../components/QueryState';
import { errorMessage } from '../format';

const roles: PortalRole[] = ['Owner', 'Member', 'Viewer'];

const roleHelp: Array<[PortalRole, string]> = [
  ['Owner', 'everything, plus people and roles'],
  ['Member', 'deployments, licenses, logs, support'],
  ['Viewer', 'read-only health and uptime'],
];

const th = { fontSize: 12, color: color.textMuted, whiteSpace: 'nowrap' } as const;
const muted = { color: color.textMuted } as const;

/**
 * The customer's people and pending invites (docs/design/CustomerTeam.dc.html).
 * Every role reads it; an Owner manages everyone but themselves and handles
 * invites, whose links are shown once.
 */
export function People({ meId, isOwner }: { meId: string; isOwner: boolean }) {
  const { data, error } = useQuery(teamQuery);
  const [managing, setManaging] = useState<TeamUser>();
  const [link, setLink] = useState<InviteResponse>();
  return (
    <Card
      padded={false}
      title={data ? `People · ${data.users.length + data.invites.length}` : 'People'}
    >
      {!data ? (
        <div style={{ padding: '0 20px 16px' }}>
          <QueryState error={error} />
        </div>
      ) : (
        <>
          <Table.ScrollContainer minWidth={640}>
            <Table verticalSpacing="sm" horizontalSpacing="md" fz={14} aria-label="People">
              <Table.Thead>
                <Table.Tr>
                  {['Name', 'Role', 'MFA', 'Last sign-in', ''].map((h, i) => (
                    <Table.Th key={i} style={th}>
                      {h}
                    </Table.Th>
                  ))}
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {data.users.map((u) => (
                  <UserRow
                    key={u.id}
                    u={u}
                    you={u.id === meId}
                    action={
                      isOwner &&
                      u.id !== meId && (
                        <Button
                          variant="subtle"
                          size="compact-sm"
                          aria-label={`Manage ${u.name}`}
                          onClick={() => setManaging(u)}
                        >
                          Manage
                        </Button>
                      )
                    }
                  />
                ))}
                {data.invites.map((i) => (
                  <InviteRow key={i.id} i={i} isOwner={isOwner} onLink={setLink} />
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
          <div
            style={{
              padding: '14px 20px',
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: 12,
              fontSize: 13,
              color: color.textBody,
              background: color.subtle,
              borderRadius: `0 0 ${radius.card}px ${radius.card}px`,
            }}
          >
            {roleHelp.map(([r, text]) => (
              <div key={r}>
                <b>{r}</b>: {text}
              </div>
            ))}
          </div>
        </>
      )}
      {managing && data && (
        <ManageModal u={managing} team={data} onClose={() => setManaging(undefined)} />
      )}
      <Modal opened={!!link} onClose={() => setLink(undefined)} title="New invite link">
        {link && (
          <InviteLink
            email={link.invite.email}
            inviteUrl={link.inviteUrl}
            expiresAt={link.invite.expiresAt}
            emailed={link.emailed}
            onDone={() => setLink(undefined)}
          />
        )}
      </Modal>
    </Card>
  );
}

function UserRow({ u, you, action }: { u: TeamUser; you: boolean; action: ReactNode }) {
  return (
    <Table.Tr>
      <Table.Td>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <span>
            <b>{u.name}</b>
            {you && <span style={muted}> (you)</span>}{' '}
            {!u.enabled && <StatusChip tone="unk">Disabled</StatusChip>}
          </span>
          <span style={{ ...muted, fontSize: 13 }}>{u.email}</span>
        </div>
      </Table.Td>
      <Table.Td>{u.role}</Table.Td>
      <Table.Td>{mfaLabel(u)}</Table.Td>
      <Table.Td style={muted}>{u.lastSignInAt ? formatAgo(u.lastSignInAt) : 'Never'}</Table.Td>
      <Table.Td>{action || <span style={muted}>–</span>}</Table.Td>
    </Table.Tr>
  );
}

function InviteRow({
  i,
  isOwner,
  onLink,
}: {
  i: TeamInvite;
  isOwner: boolean;
  onLink: (r: InviteResponse) => void;
}) {
  const qc = useQueryClient();
  const refresh = () => qc.invalidateQueries({ queryKey: teamQuery.queryKey });
  const resend = useMutation({
    mutationFn: () => api.team.resendInvite(i.id),
    onSuccess: async (r) => {
      onLink(r);
      await refresh();
    },
  });
  const revoke = useMutation({ mutationFn: () => api.team.revokeInvite(i.id), onSuccess: refresh });
  const error = resend.error ?? revoke.error;
  return (
    <>
      <Table.Tr>
        <Table.Td>
          <b>{i.email}</b>
        </Table.Td>
        <Table.Td>{i.role}</Table.Td>
        <Table.Td>
          <StatusChip tone="warn">Pending setup</StatusChip>
        </Table.Td>
        <Table.Td style={muted}>Invite expires {formatDate(i.expiresAt)}</Table.Td>
        <Table.Td>
          {isOwner ? (
            <Group gap={4} wrap="nowrap">
              <Button
                variant="subtle"
                size="compact-sm"
                aria-label={`Resend invite to ${i.email}`}
                loading={resend.isPending}
                onClick={() => resend.mutate()}
              >
                Resend invite
              </Button>
              <Button
                variant="subtle"
                color="red"
                size="compact-sm"
                aria-label={`Revoke invite to ${i.email}`}
                loading={revoke.isPending}
                onClick={() => revoke.mutate()}
              >
                Revoke
              </Button>
            </Group>
          ) : (
            <span style={muted}>–</span>
          )}
        </Table.Td>
      </Table.Tr>
      {error && (
        <Table.Tr>
          <Table.Td colSpan={5}>
            <Alert color="red">{errorMessage(error)}</Alert>
          </Table.Td>
        </Table.Tr>
      )}
    </>
  );
}

/** Role and sign-in for one teammate; the server refuses removing the last enabled Owner. */
function ManageModal({ u, team, onClose }: { u: TeamUser; team: PortalTeam; onClose: () => void }) {
  const qc = useQueryClient();
  const [role, setRole] = useState<PortalRole>(u.role);
  const [enabled, setEnabled] = useState(u.enabled);
  const locked = isLastOwner(u, team.users);
  const save = useMutation({
    mutationFn: () =>
      api.team.updateUser(u.id, {
        ...(role !== u.role ? { role } : {}),
        ...(enabled !== u.enabled ? { enabled } : {}),
      }),
    onSuccess: async (next) => {
      qc.setQueryData<PortalTeam>(teamQuery.queryKey, (t) =>
        t ? { ...t, users: t.users.map((x) => (x.id === next.id ? next : x)) } : t,
      );
      await qc.invalidateQueries({ queryKey: teamQuery.queryKey });
      onClose();
    },
  });
  const changed = role !== u.role || enabled !== u.enabled;
  return (
    <Modal opened onClose={onClose} title={`Manage ${u.name}`}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (changed) save.mutate();
          else onClose();
        }}
      >
        <Stack gap={16}>
          <Text size="sm" c="dimmed">
            {u.email}
          </Text>
          <div>
            <Text size="sm" fw={500} mb={6}>
              Role
            </Text>
            <SegmentedControl
              fullWidth
              data={roles}
              value={role}
              disabled={locked}
              onChange={(v) => setRole(v as PortalRole)}
            />
          </div>
          <Switch
            label="Can sign in"
            description="Turning this off signs them out everywhere."
            checked={enabled}
            disabled={locked}
            onChange={(e) => setEnabled(e.currentTarget.checked)}
          />
          {locked && (
            <Text size="sm" c="dimmed">
              {u.name} is the only enabled Owner. Make someone else an Owner first.
            </Text>
          )}
          {save.error && <Alert color="red">{errorMessage(save.error)}</Alert>}
          <Group justify="flex-end">
            <Button variant="default" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" loading={save.isPending}>
              Save
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}

/** "Invite someone": email and role; the link is emailed and also shown once. */
export function InviteModal({ opened, onClose }: { opened: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<PortalRole>('Viewer');
  const [emailError, setEmailError] = useState<string>();
  // Shown once: held only in this component, never in the query cache.
  const [sent, setSent] = useState<InviteResponse>();
  const invite = useMutation({
    mutationFn: () => api.team.invite({ email: email.trim(), role }),
    onSuccess: async (r) => {
      setSent(r);
      await qc.invalidateQueries({ queryKey: teamQuery.queryKey });
    },
    onError: (e) => {
      const f = (e as { fields?: Record<string, string> }).fields;
      if (f?.email) setEmailError('Enter a valid email address.');
    },
  });
  const close = () => {
    setEmail('');
    setRole('Viewer');
    setSent(undefined);
    setEmailError(undefined);
    invite.reset();
    onClose();
  };
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return setEmailError('Enter their email address.');
    setEmailError(undefined);
    invite.mutate();
  };
  return (
    <Modal opened={opened} onClose={close} title="Invite someone">
      {sent ? (
        <InviteLink
          email={sent.invite.email}
          inviteUrl={sent.inviteUrl}
          expiresAt={sent.invite.expiresAt}
          emailed={sent.emailed}
          onDone={close}
        />
      ) : (
        <form
          onSubmit={submit}
          noValidate
          style={{ display: 'flex', flexDirection: 'column', gap: 14 }}
        >
          <TextInput
            label="Email"
            type="email"
            data-autofocus
            value={email}
            error={emailError}
            onChange={(e) => setEmail(e.currentTarget.value)}
          />
          <Select
            label="Role"
            data={roles}
            value={role}
            allowDeselect={false}
            onChange={(v) => v && setRole(v as PortalRole)}
          />
          <Text size="xs" c="dimmed">
            If your organization signs in through single sign-on, they can use it with this email
            instead of setting a password.
          </Text>
          {invite.error && !emailError && <Alert color="red">{errorMessage(invite.error)}</Alert>}
          <Group justify="flex-end">
            <Button variant="default" onClick={close}>
              Cancel
            </Button>
            <Button type="submit" loading={invite.isPending}>
              Create invite
            </Button>
          </Group>
        </form>
      )}
    </Modal>
  );
}
